/* SafeCastle Search System v3.0 — Standalone Autocomplete */

// ─── Config ───────────────────────────────────────────────────────────────────
const SC_POPULAR = window.SC_POPULAR_SEARCHES || [
  'Mountain House', 'freeze dried food', 'emergency kit', 'Berkey filter',
  'canned chicken', 'water storage', '72 hour kit', 'protein', 'MRE', 'survival food'
];

// ─── Personalization ──────────────────────────────────────────────────────────
class SCPersonalization {
  constructor() {
    this._key = 'sc_signals_v1';
    this.signals = this._load();
  }
  _load() {
    try { return JSON.parse(localStorage.getItem(this._key)) || { vendors:{}, types:{}, queries:[] }; }
    catch(e) { return { vendors:{}, types:{}, queries:[] }; }
  }
  _save() {
    try { localStorage.setItem(this._key, JSON.stringify(this.signals)); } catch(e) {}
  }
  recordView(p) {
    if (p.vendor) this.signals.vendors[p.vendor] = (this.signals.vendors[p.vendor]||0)+1;
    if (p.product_type) this.signals.types[p.product_type] = (this.signals.types[p.product_type]||0)+1;
    this._save();
  }
  recordSearch(q) {
    this.signals.queries = [q, ...this.signals.queries.filter(x=>x!==q)].slice(0,10);
    this._save();
  }
}

// ─── Analytics ────────────────────────────────────────────────────────────────
class SCAnalytics {
  constructor(supabaseUrl, supabaseKey) {
    this.su = supabaseUrl; this.sk = supabaseKey;
  }
  _sid() {
    let s = sessionStorage.getItem('sc_sid');
    if (!s) { s=Math.random().toString(36).slice(2)+Date.now().toString(36); sessionStorage.setItem('sc_sid',s); }
    return s;
  }
  _ga4(event, data) { if (typeof gtag!=='undefined') gtag('event',event,data); }
  _db(event, data) {
    if (!this.su||!this.sk) return;
    fetch(`${this.su}/rest/v1/sc_search_events`,{
      method:'POST',
      headers:{'apikey':this.sk,'Authorization':`Bearer ${this.sk}`,'Content-Type':'application/json','Prefer':'return=minimal'},
      body:JSON.stringify({event_type:event,query:data.query||null,product_id:data.pid||null,
        result_count:data.count!=null?Number(data.count):null,position:data.pos!=null?Number(data.pos):null,
        session_id:this._sid(),created_at:new Date().toISOString()})
    }).catch(()=>{});
  }
  trackSearch(q,n) { this._ga4('search',{search_term:q,result_count:n}); this._db('search',{query:q,count:n}); }
  trackClick(q,pid,pos) { this._ga4('select_item',{search_term:q,item_id:pid,index:pos}); this._db('click',{query:q,pid,pos}); }
}

// ─── Standalone Autocomplete ──────────────────────────────────────────────────
class SCAutocomplete {
  constructor(input) {
    this.input   = input;
    this.form    = input.closest('form');
    this.cache   = new Map();
    this.timer   = null;
    this.current = '';
    this.ac      = null; // active index for keyboard nav
    this._build();
    this._bind();
  }

  // Create the dropdown DOM node
  _build() {
    this.dropdown = document.createElement('div');
    this.dropdown.className = 'sc-ac';
    this.dropdown.setAttribute('role', 'listbox');

    // Ensure the parent form is relatively positioned so dropdown anchors correctly
    if (this.form) {
      if (getComputedStyle(this.form).position === 'static') this.form.style.position = 'relative';
      this.form.appendChild(this.dropdown);
    } else {
      if (getComputedStyle(this.input.parentElement).position === 'static') this.input.parentElement.style.position = 'relative';
      this.input.parentElement.appendChild(this.dropdown);
    }
  }

  _bind() {
    this.input.addEventListener('focus', () => this._onFocus());
    this.input.addEventListener('input', () => this._onInput());
    this.input.addEventListener('keydown', e => this._onKey(e));

    // Close when clicking outside
    document.addEventListener('click', e => {
      if (!this.dropdown.contains(e.target) && e.target !== this.input) this._hide();
    });

    // Close on form submit
    this.form?.addEventListener('submit', () => this._hide());
  }

  _onFocus() {
    const q = this.input.value.trim();
    if (q.length === 0) this._showPre();
    else if (q.length >= 2) this._fetch(q);
  }

  _onInput() {
    clearTimeout(this.timer);
    const q = this.input.value.trim();
    if (q.length === 0) { this._showPre(); return; }
    if (q.length < 2) { this._hide(); return; }
    this.timer = setTimeout(() => this._fetch(q), 220);
  }

  _onKey(e) {
    const open = this.dropdown.classList.contains('sc-ac--open');
    if (!open) return;
    const items = [...this.dropdown.querySelectorAll('[role="option"]')];
    const cur = this.dropdown.querySelector('.sc-ac--active');
    const idx = cur ? items.indexOf(cur) : -1;

    if (e.key === 'ArrowDown') {
      e.preventDefault();
      this._setActive(items[Math.min(idx+1, items.length-1)]);
    } else if (e.key === 'ArrowUp') {
      e.preventDefault();
      this._setActive(idx > 0 ? items[idx-1] : null);
    } else if (e.key === 'Enter') {
      if (cur) { e.preventDefault(); cur.click(); }
    } else if (e.key === 'Escape') {
      this._hide(); this.input.blur();
    }
  }

  _setActive(el) {
    this.dropdown.querySelectorAll('[role="option"]').forEach(i => {
      i.classList.remove('sc-ac--active');
      i.setAttribute('aria-selected', 'false');
    });
    if (el) {
      el.classList.add('sc-ac--active');
      el.setAttribute('aria-selected', 'true');
      el.scrollIntoView({ block: 'nearest' });
    }
  }

  // ── Pre-search panel ──────────────────────────────────────────────────────
  _showPre() {
    const recent = window._scPersonalization?.signals?.queries || [];
    if (recent.length === 0 && SC_POPULAR.length === 0) { this._hide(); return; }

    let html = '<div class="sc-pre">';

    if (recent.length > 0) {
      html += `<div class="sc-pre__section">
        <div class="sc-pre__label">
          <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="10" cy="10" r="7" stroke="currentColor" stroke-width="1.5"/><path d="M10 6v4l2.5 2.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
          Recent Searches
          <button class="sc-pre__clear" data-sc-clear type="button">Clear</button>
        </div>
        <div class="sc-pre__chips">
          ${recent.slice(0,8).map(q=>`<button class="sc-chip sc-chip--recent" data-chip="${this._e(q)}" type="button">${this._e(q)}</button>`).join('')}
        </div>
      </div>`;
    }

    html += `<div class="sc-pre__section">
      <div class="sc-pre__label">
        <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.75" stroke="currentColor" stroke-width="1.5"/><path d="M13 13L17 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
        Popular Searches
      </div>
      <div class="sc-pre__chips">
        ${SC_POPULAR.map(q=>`<button class="sc-chip sc-chip--popular" data-chip="${this._e(q)}" type="button">${this._e(q)}</button>`).join('')}
      </div>
    </div></div>`;

    this.dropdown.innerHTML = html;
    this._show();

    // Chip clicks
    this.dropdown.querySelectorAll('[data-chip]').forEach(chip => {
      chip.addEventListener('mousedown', e => {
        e.preventDefault();
        this.input.value = chip.dataset.chip;
        this._fetch(chip.dataset.chip);
      });
    });

    // Clear recent
    this.dropdown.querySelector('[data-sc-clear]')?.addEventListener('mousedown', e => {
      e.preventDefault();
      if (window._scPersonalization) { window._scPersonalization.signals.queries=[]; window._scPersonalization._save(); }
      this._showPre();
    });
  }

  // ── Fetch + Render ────────────────────────────────────────────────────────
  async _fetch(query) {
    if (this.current === query) return;
    this.current = query;

    // Show loading state
    this.dropdown.innerHTML = '<div class="sc-ac__loading"><div class="sc-ac__spinner"></div></div>';
    this._show();

    // Check cache
    if (this.cache.has(query)) {
      this._render(query, this.cache.get(query));
      return;
    }

    try {
      const params = new URLSearchParams({
        q: query,
        'resources[type]': 'product,query,collection',
        'resources[limit]': '6',
        'resources[options][fields]': 'title,vendor,product_type,variants.sku',
        'resources[options][unavailable_products]': 'last',
      });
      const res = await fetch(`/search/suggest.json?${params}`);
      if (!res.ok) throw new Error(res.status);
      const json = await res.json();
      const data = json.resources?.results || {};
      this.cache.set(query, data);
      if (this.current === query) this._render(query, data);
    } catch(e) { this._hide(); }
  }

  _render(query, data) {
    const products    = data.products    || [];
    const queries     = data.queries     || [];
    const collections = data.collections || [];

    if (!products.length && !queries.length && !collections.length) {
      this.dropdown.innerHTML = `<div class="sc-ac__empty">No results for "<strong>${this._e(query)}</strong>"</div>`;
      return;
    }

    let html = '';

    // ── Query suggestions
    if (queries.length) {
      html += '<div class="sc-ac__section">';
      queries.slice(0,5).forEach(q => {
        html += `<a href="${q.url}" class="sc-ac__row sc-ac__suggestion" role="option" aria-selected="false" tabindex="-1">
          <svg class="sc-ac__row-icon" viewBox="0 0 20 20" fill="none" aria-hidden="true"><circle cx="8.5" cy="8.5" r="5.75" stroke="currentColor" stroke-width="1.5"/><path d="M13 13L17 17" stroke="currentColor" stroke-width="1.5" stroke-linecap="round"/></svg>
          <span>${this._hl(q.text, query)}</span>
        </a>`;
      });
      html += '</div>';
    }

    // ── Collections
    if (collections.length) {
      html += '<div class="sc-ac__label">Collections</div><div class="sc-ac__section">';
      collections.slice(0,3).forEach(c => {
        const colImg = c.image ? this._img(c.image, 120) : '';
        html += `<a href="${c.url}" class="sc-ac__product sc-ac__collection" role="option" aria-selected="false" tabindex="-1">
          <div class="sc-ac__col-thumb">
            ${colImg
              ? `<img src="${colImg}" alt="${this._e(c.title)}" width="60" height="60" loading="lazy">`
              : `<svg viewBox="0 0 24 24" fill="none" aria-hidden="true" class="sc-ac__col-icon">
                  <rect x="3" y="3" width="8" height="8" rx="2" fill="#E8621A"/>
                  <rect x="13" y="3" width="8" height="8" rx="2" fill="#E8621A" opacity="0.65"/>
                  <rect x="3" y="13" width="8" height="8" rx="2" fill="#E8621A" opacity="0.65"/>
                  <rect x="13" y="13" width="8" height="8" rx="2" fill="#E8621A" opacity="0.3"/>
                </svg>`
            }
          </div>
          <div class="sc-ac__product-body">
            <div class="sc-ac__col-eyebrow">Collection</div>
            <div class="sc-ac__title">${this._hl(c.title, query)}</div>
            <div class="sc-ac__col-cta">Browse all &rarr;</div>
          </div>
        </a>`;
      });
      html += '</div>';
    }

    // ── Products
    if (products.length) {
      html += '<div class="sc-ac__label">Products</div><div class="sc-ac__section">';
      products.forEach((p, i) => {
        const price   = this._money(p.price);
        const compare = p.compare_at_price_max && parseFloat(p.compare_at_price_max) > parseFloat(p.price)
          ? this._money(p.compare_at_price_max) : '';
        const save    = compare ? Math.round((1 - parseFloat(p.price)/parseFloat(p.compare_at_price_max))*100) : 0;
        const img     = p.image ? this._img(p.image, 120) : '';

        html += `<a href="${p.url}" class="sc-ac__product" role="option" aria-selected="false" tabindex="-1"
            data-pid="${p.id}" data-handle="${p.handle}" data-pos="${i}">
          <div class="sc-ac__product-img">
            ${img ? `<img src="${img}" alt="${this._e(p.title)}" width="60" height="60" loading="lazy">` : '<div class="sc-ac__img-placeholder"></div>'}
            ${save>0 ? `<span class="sc-ac__badge">-${save}%</span>` : ''}
          </div>
          <div class="sc-ac__product-body">
            ${p.vendor ? `<div class="sc-ac__vendor">${this._e(p.vendor)}</div>` : ''}
            <div class="sc-ac__title">${this._hl(p.title, query)}</div>
            <div class="sc-ac__price-row">
              <span class="sc-ac__price">${price}</span>
              ${compare ? `<span class="sc-ac__compare">${compare}</span><span class="sc-ac__save">Save ${save}%</span>` : ''}
            </div>
          </div>
        </a>`;
      });
      html += '</div>';
    }

    // ── Footer
    html += `<a href="/search?q=${encodeURIComponent(query)}&type=product" class="sc-ac__footer" role="option" aria-selected="false">
      <span>See all results for "<strong>${this._e(query)}</strong>"</span>
      <svg viewBox="0 0 20 20" fill="none" aria-hidden="true"><path d="M4 10h12M11 5l5 5-5 5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>
    </a>`;

    this.dropdown.innerHTML = html;
    this._show();

    // Analytics + personalization on product click
    this.dropdown.querySelectorAll('[data-pid]').forEach(el => {
      el.addEventListener('click', () => {
        window._scAnalytics?.trackClick(query, el.dataset.pid, parseInt(el.dataset.pos));
        window._scPersonalization?.recordSearch(query);
      });
    });

    window._scAnalytics?.trackSearch(query, products.length);
  }

  _show() { this.dropdown.classList.add('sc-ac--open'); }
  _hide() { this.dropdown.classList.remove('sc-ac--open'); this.current = ''; }

  _money(val) {
    const n = parseFloat(val);
    if (isNaN(n)) return '';
    // /search/suggest.json returns prices in the store's currency unit (dollars for USD), not cents
    return '$' + n.toFixed(2).replace(/\.00$/, '');
  }

  _img(url, size) {
    if (!url) return '';
    // Insert _120x120 before the extension
    return url.replace(/(\.(jpe?g|png|gif|webp))(\?|$)/i, `_${size}x${size}$1$3`);
  }

  _e(str) {
    return String(str).replace(/&/g,'&amp;').replace(/</g,'&lt;').replace(/>/g,'&gt;').replace(/"/g,'&quot;');
  }

  _hl(text, query) {
    const pat = query.replace(/[.*+?^${}()|[\]\\]/g,'\\$&');
    return this._e(text).replace(new RegExp(`(${pat})`,'gi'),'<mark>$1</mark>');
  }
}

// ─── Search Results Page (/pages/search-results) ──────────────────────────────
class SCSearchResults {
  constructor(container) {
    this.container = container;
    this.params = new URLSearchParams(window.location.search);
    this.query = this.params.get('q') || '';
    this.page  = parseInt(this.params.get('page')||'1');
  }

  async init() {
    document.querySelectorAll('[data-sc-query-display]').forEach(el => el.textContent = this.query||'All Products');
    document.querySelectorAll('[data-sc-query-input]').forEach(el => { el.value = this.query; });

    if (!this.query) {
      this.container.innerHTML = '<div class="sc-serp-empty"><p>Enter a search term above to find products.</p></div>';
      return;
    }

    this._loading();
    try {
      const html = await this._fetch(this.query, this.page);
      this._inject(html);
      window._scAnalytics?.trackSearch(this.query, this.container.querySelectorAll('.sc-pc').length);
      window._scPersonalization?.recordSearch(this.query);
    } catch(e) {
      this.container.innerHTML = `<div class="sc-serp-error"><p>Search unavailable. <a href="/search?q=${encodeURIComponent(this.query)}" style="color:#E8621A">View results here</a></p></div>`;
    }
  }

  async _fetch(query, page) {
    const p = new URLSearchParams({ q:query, type:'product', page });
    const res = await fetch(`/search?${p}&section_id=main-search`);
    if (!res.ok) throw new Error(res.status);
    const text = await res.text();
    const doc = new DOMParser().parseFromString(text,'text/html');
    const wrap = doc.querySelector('#shopify-section-main-search');
    return wrap ? wrap.innerHTML : doc.body.innerHTML;
  }

  _inject(html) {
    const tmp = document.createElement('div');
    tmp.innerHTML = html;
    tmp.querySelectorAll('.sc-search-hero').forEach(el => el.remove());
    this.container.innerHTML = tmp.innerHTML;
    this.container.querySelectorAll('script').forEach(old => {
      const s = document.createElement('script');
      Array.from(old.attributes).forEach(a => s.setAttribute(a.name, a.value));
      s.textContent = old.textContent;
      old.replaceWith(s);
    });
    this._bindPagination();
    this._bindClicks();
  }

  _bindPagination() {
    this.container.querySelectorAll('a[href*="/search?"]').forEach(link => {
      link.addEventListener('click', async e => {
        const url = new URL(link.getAttribute('href'), location.origin);
        if (url.pathname !== '/search') return;
        e.preventDefault();
        const q = url.searchParams.get('q')||this.query;
        const pg = url.searchParams.get('page')||'1';
        const newUrl = new URL(location.href);
        newUrl.searchParams.set('q', q); newUrl.searchParams.set('page', pg);
        history.pushState({}, '', newUrl.toString());
        scrollTo({ top:0, behavior:'smooth' });
        this._loading();
        const html = await this._fetch(q, pg).catch(()=>null);
        if (html) this._inject(html);
      });
    });
  }

  _bindClicks() {
    this.container.querySelectorAll('a[href*="/products/"]').forEach((link, i) => {
      link.addEventListener('click', () => {
        const id = link.href.split('/products/')[1]?.split('?')[0];
        window._scAnalytics?.trackClick(this.query, id, i);
      });
    });
  }

  _loading() {
    this.container.innerHTML = `<div class="sc-serp-loading"><div class="sc-serp-spinner"></div><p>Searching for "${this._e(this.query)}"…</p></div>`;
  }

  _e(s) { return String(s).replace(/</g,'&lt;').replace(/>/g,'&gt;'); }
}

// ─── Init ─────────────────────────────────────────────────────────────────────
window._scPersonalization = new SCPersonalization();
window._scAnalytics = new SCAnalytics(window.SC_SUPABASE_URL||null, window.SC_SUPABASE_KEY||null);

document.addEventListener('DOMContentLoaded', () => {
  // Attach standalone autocomplete to every search input in the header
  document.querySelectorAll('input[name="q"][type="search"]').forEach(input => {
    new SCAutocomplete(input);
  });

  // SERP page
  const serp = document.querySelector('[data-sc-search-results]');
  if (serp) new SCSearchResults(serp).init();

  if (window.scCurrentProduct) window._scPersonalization.recordView(window.scCurrentProduct);
});
